import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { BRANDS_DATA } from "@/data/brandsData";

const CATEGORIES = ["All", "Lighting & Electricals", "Electricals & Starters", "Suspension & Ride Control", "Rubber & Sealing", "General Spare Parts", "Car Care Products", "Electricals", "Bearings", "Auto Components", "Auto Electricals", "Cables & Wiring", "Cables & Controls", "Sealing & Vibration Control", "Engine Parts", "Fuel System Components", "Horns & Accessories"];

export default function BrandsPage() {
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredBrands = activeCategory === "All"
    ? BRANDS_DATA
    : BRANDS_DATA.filter(b => b.category === activeCategory);

  const usedCategories = ["All", ...new Set(BRANDS_DATA.map(b => b.category))];

  return (
    <div data-testid="brands-page" style={{ marginTop: "calc(36px + 96px)" }}>
      {/* Hero */}
      <section className="py-16 md:py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="font-mono text-xs tracking-[0.3em] text-neutral-500 uppercase block mb-3">
            Brand Portfolio
          </span>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl uppercase tracking-tight">
            Our Authorized Brands
          </h1>
          <p className="text-neutral-400 mt-4 max-w-2xl text-base md:text-lg">
            We are proud distributors of India's most trusted automotive aftermarket brands. 
            Every product we supply is 100% genuine and backed by manufacturer warranty.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white border-b border-neutral-200 sticky top-[100px] z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 overflow-x-auto py-4 scrollbar-hide">
            {usedCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                className={`whitespace-nowrap px-4 py-2 text-xs font-heading uppercase tracking-wider font-semibold transition-colors ${
                  activeCategory === cat
                    ? "bg-black text-white"
                    : "bg-neutral-100 text-neutral-500 hover:bg-neutral-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredBrands.map((brand) => (
              <div
                key={brand.name}
                data-testid={`brand-detail-${brand.name.toLowerCase().replace(/\s/g, "-")}`}
                className="border border-neutral-200 p-8 hover:border-black transition-colors duration-300 group"
              >
                <div className="flex items-center justify-between mb-6">
                  {brand.logo_url ? (
                    <img
                      src={brand.logo_url}
                      alt={`${brand.name} logo`}
                      className={`object-contain ${["Rideon", "Gabriel", "AE", "Remsons", "Ars-HTA"].includes(brand.name) ? "max-h-28 max-w-[200px]" : "max-h-14 max-w-[140px]"}`}
                    />
                  ) : (
                    <span className="font-heading font-extrabold text-2xl text-black uppercase tracking-tight">
                      {brand.name}
                    </span>
                  )}
                  <span className="font-mono text-[9px] tracking-[0.15em] text-neutral-400 uppercase bg-neutral-100 px-2 py-1">
                    {brand.category}
                  </span>
                </div>
                <p className="text-sm text-neutral-500 leading-relaxed mb-6">
                  {brand.description}
                </p>
                {brand.website && (
                  <a
                    href={brand.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-heading uppercase tracking-wider font-semibold text-neutral-400 hover:text-black transition-colors"
                  >
                    Visit Brand <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            ))}
          </div>
          {filteredBrands.length === 0 && (
            <p className="text-center text-neutral-400 py-12">No brands found for this category.</p>
          )}
        </div>
      </section>

      {/* Download CTA */}
      <section className="py-16 md:py-20 bg-neutral-50 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-extrabold text-2xl sm:text-3xl uppercase tracking-tight text-black mb-4">
            Need Our Complete Product Catalog?
          </h2>
          <p className="text-neutral-500 mb-8 max-w-lg mx-auto">
            Download our latest catalog with complete product listings, part numbers, and pricing for all authorized brands.
          </p>
          <button
            data-testid="download-catalog-btn"
            className="bg-black text-white hover:bg-neutral-800 h-12 px-10 text-sm font-heading uppercase font-bold tracking-wide cta-btn inline-flex items-center"
            onClick={() => alert("Catalog will be available for download soon. Please contact us for the latest product list.")}
          >
            Download Catalog
          </button>
        </div>
      </section>
    </div>
  );
}

